/**
 * Smith Protective — Dashboard (Iter159 Fase 1a).
 *
 * Landing screen for the security-industry BD manager. Answers "Who should
 * I call/visit today?" per Section 40 of the brief.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Shield, Flame, TrendingUp, DollarSign, AlertTriangle, MapPin, Users,
  Building2, Landmark, ArrowRight, Loader2, RefreshCw, Warehouse, Zap,
  Sparkles, Landmark as LandmarkIcon, MailWarning, Rocket,
} from "lucide-react";
import Sidebar from "../../components/Sidebar";
import AppHeader from "../../components/SmithAppHeader";
import SmithImportExport from "../../components/SmithImportExport";
import MultiSiteAccountsCard from "../../components/MultiSiteAccountsCard";
import { LanguageMixCard } from "./LanguageMixCard";
import ClaireActionsWidget from "../../components/ClaireActionsWidget";
import SmithCommandCenter from "../../components/smith/SmithCommandCenter";
import InstallNudgeBanner from "../../components/InstallNudgeBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const usd = (n) => n ? `$${Math.round(n).toLocaleString("en-US")}` : "—";

export default function SmithDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);   // Iter256 · std header
  const [seeding, setSeeding] = useState(false);
  const [scraping, setScraping] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [scrapingAll, setScrapingAll] = useState(false);
  const [scrapeMetro, setScrapeMetro] = useState("orlando_tampa");
  // Iter237 · Enrichment Gatekeeper — coverage stats + one-click sweep
  const [missing, setMissing] = useState(null);          // {high_value_total, missing_contact, coverage_pct}
  const [enrichingHV, setEnrichingHV] = useState(false);
  const [metros, setMetros] = useState([
    { id: "orlando_tampa", label: "Orlando + Tampa" },
    { id: "miami", label: "Miami · Dade+Broward" },
    { id: "jacksonville", label: "Jacksonville" },
    { id: "fort_lauderdale", label: "Fort Lauderdale" },
  ]);
  useEffect(() => {
    axios.get(`${API}/smith/scrape/metros`)
      .then(({ data }) => data?.metros && setMetros(data.metros))
      .catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/smith/dashboard`);
      setData(data);
      setLastUpdated(new Date());               // Iter256 · std header timestamp
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Smith dashboard");
    } finally {
      setLoading(false);
    }
    // Iter237 · pull coverage stats in parallel; failure is silent.
    try {
      const { data: m } = await axios.get(
        `${API}/smith/find-email/missing-contact-stats?min_acv=250000`);
      setMissing(m);
    } catch { /* silent */ }
  };

  // Iter237 · One-click "Enrich High-Value" — fires the sweep-all worker
  // with min_acv=$250k so scarce enrichment credits are focused on the
  // biggest deals first.
  const enrichHighValue = async () => {
    if (!missing || missing.missing_contact === 0) {
      toast.info("Nenhum lead high-value sem contato — cobertura já 100%!");
      return;
    }
    if (!confirm(`Rodar enrichment em background nos ${missing.missing_contact} lead(s) high-value (ACV ≥ $250k) sem contato?`)) return;
    setEnrichingHV(true);
    try {
      const { data } = await axios.post(
        `${API}/smith/find-email/sweep-all`,
        { min_acv: 250000, auto_archive: true });
      if (!data.job_id) {
        toast.info(data.message || "Nada a processar");
        return;
      }
      if (data.already_running) {
        toast.info(`Já existe um sweep rodando · targets: ${data.total_targets}`);
      } else {
        toast.success(`Enrichment iniciado em ${data.total_targets} lead(s) high-value`);
      }
      // Optimistic UI refresh a few seconds later so the KPI reflects progress.
      setTimeout(load, 15000);
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Falha ao iniciar sweep");
    } finally { setEnrichingHV(false); }
  };
  const seed = async () => {
    setSeeding(true);
    try {
      const { data } = await axios.post(`${API}/smith/seed`, {});
      toast.success(`Seeded ${data.inserted} prospects (total ${data.total})`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Seed failed");
    } finally {
      setSeeding(false);
    }
  };
  const scrapeWarehouses = async () => {
    setScraping(true);
    try {
      const { data } = await axios.post(`${API}/smith/scrape/warehouses?limit=25&metro=${scrapeMetro}`, {});
      toast.success(`Warehouse scraper rodando: ${data.label} — aguarde ~30s`);
      setTimeout(() => load(), 30000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scrape failed");
    } finally {
      setTimeout(() => setScraping(false), 30000);
    }
  };
  const scanTriggers = async () => {
    setScanning(true);
    try {
      await axios.post(`${API}/smith/triggers/scan`, {});
      toast.success("Trigger watchdog rodando — atualizando triggers de cada conta");
      setTimeout(() => load(), 20000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed");
    } finally {
      setTimeout(() => setScanning(false), 20000);
    }
  };
  const enrichOsm = async () => {
    setEnriching(true);
    try {
      await axios.post(`${API}/smith/enrich/scan?only_osm=true`, {});
      toast.success("Enriquecendo warehouses OSM com Google Places + regra sqft");
      setTimeout(() => load(), 30000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enrich failed");
    } finally {
      setTimeout(() => setEnriching(false), 30000);
    }
  };
  const scrapeAllMetros = async () => {
    if (!window.confirm("Rodar scraper OSM em TODOS os 7 metros (FL+TX+GA)? Vai levar ~3min.")) return;
    setScrapingAll(true);
    try {
      await axios.post(`${API}/smith/scrape/all-metros?per_metro=20`, {});
      toast.success("Scrape multi-metro rodando em background — atualize em ~3min");
      setTimeout(() => load(), 90000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scrape all failed");
    } finally {
      setTimeout(() => setScrapingAll(false), 90000);
    }
  };
  // Iter172 — Property Appraiser scrape (Miami-Dade, Hillsborough)
  const [paScraping, setPaScraping] = useState(false);
  const [paUsePrefix, setPaUsePrefix] = useState("48");
  const [paCounty, setPaCounty] = useState("miami-dade");
  const scrapePropertyAppraiser = async () => {
    setPaScraping(true);
    try {
      await axios.post(`${API}/smith/property-appraiser/${paCounty}/scrape?use_prefix=${paUsePrefix}&limit=50`);
      const label = paCounty === "miami-dade" ? "Miami-Dade" : "Hillsborough";
      toast.success(`${label} PA scrape iniciado · DOR ${paUsePrefix}xx · 50 registros`);
      setTimeout(() => load(), 20000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PA scrape failed");
    } finally {
      setTimeout(() => setPaScraping(false), 20000);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen" style={{ background: "#0B1120", color: "#E2E8F0" }}>
      <Sidebar />
      <div className="lg:ml-[var(--al-sidebar-w,260px)]">
        <AppHeader
          testid="smith-dashboard-header"
          title="Smith Protective"
          subtitle="Market Intelligence · Discover, prioritize and convert high-value security opportunities"
          claireQuestion="Como uso o Smith Protective Dashboard — como priorizo prospects, o que faz cada botão da toolbar?"
          lastUpdated={lastUpdated}
          onRefresh={load}
          refreshing={loading}
          logo={
            <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                 style={{ background: "linear-gradient(135deg,#1E3A8A 0%,#0F172A 100%)", color: "#DC2626" }}>
              <Shield className="w-5 h-5" />
            </div>
          }
          mobileMenu={
            <button type="button" onClick={load}
              data-testid="smith-refresh-mobile"
              className="w-full flex items-center gap-2 px-3 h-10 rounded-md text-[13px] hover:bg-white/5">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
          }
        >
          <Link to="/smith/prospects" data-testid="smith-prospects-nav"
            className="h-9 px-3 rounded-lg border border-white/15 text-[12.5px] font-bold text-white hover:border-[#DC2626]">
            Prospects
          </Link>
          <Link to="/smith/kanban" data-testid="smith-kanban-nav"
            className="h-9 px-3 rounded-lg border border-white/15 text-[12.5px] font-bold text-white hover:border-[#DC2626]">
            Kanban
          </Link>
          <Link to="/smith/map" data-testid="smith-map-nav"
            className="h-9 px-3 rounded-lg border border-white/15 text-[12.5px] font-bold text-white hover:border-[#DC2626]">
            Territory Map
          </Link>
          <select value={scrapeMetro} onChange={(e) => setScrapeMetro(e.target.value)}
            data-testid="smith-metro-select"
            className="h-9 px-2 rounded-lg bg-black/30 border border-white/15 text-[12px] font-bold text-white focus:border-[#F59E0B] outline-none">
            {metros.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
          <button type="button" onClick={scrapeWarehouses} disabled={scraping}
            data-testid="smith-scrape-warehouses"
            title="Import warehouses from OSM for the selected FL metro"
            className="h-9 px-3 rounded-lg font-bold text-[12.5px] text-white inline-flex items-center gap-1.5 disabled:opacity-50"
            style={{ background: "linear-gradient(135deg,#F59E0B 0%,#B45309 100%)" }}>
            {scraping ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Warehouse className="w-3.5 h-3.5" />}
            Scrape
          </button>
          <button type="button" onClick={scanTriggers} disabled={scanning}
            data-testid="smith-scan-triggers"
            title="Scan Indeed + Google News for buying signals"
            className="h-9 px-3 rounded-lg font-bold text-[12.5px] text-white inline-flex items-center gap-1.5 disabled:opacity-50"
            style={{ background: "linear-gradient(135deg,#3B82F6 0%,#1D4ED8 100%)" }}>
            {scanning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
            Scan Triggers
          </button>
          <button type="button" onClick={enrichOsm} disabled={enriching}
            data-testid="smith-enrich-osm"
            title="Enrich OSM-imported warehouses with Google Places + sqft-based employee estimates"
            className="h-9 px-3 rounded-lg font-bold text-[12.5px] text-white inline-flex items-center gap-1.5 disabled:opacity-50"
            style={{ background: "linear-gradient(135deg,#16A34A 0%,#166534 100%)" }}>
            {enriching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            Enrich OSM
          </button>
          <button type="button" onClick={scrapeAllMetros} disabled={scrapingAll}
            data-testid="smith-scrape-all-metros"
            title="Kick off the nightly job right now: scrape all 7 FL/TX/GA metros"
            className="h-9 px-3 rounded-lg font-bold text-[12.5px] text-white inline-flex items-center gap-1.5 disabled:opacity-50"
            style={{ background: "linear-gradient(135deg,#8B5CF6 0%,#5B21B6 100%)" }}>
            {scrapingAll ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Warehouse className="w-3.5 h-3.5" />}
            Scrape All (7)
          </button>
          {/* Iter172 — Property Appraiser (Miami-Dade + Hillsborough) */}
          <div className="inline-flex items-stretch rounded-lg overflow-hidden border border-white/15"
               data-testid="smith-pa-widget">
            <select value={paCounty} onChange={(e) => setPaCounty(e.target.value)}
              data-testid="smith-pa-county"
              className="h-9 px-2 bg-black/30 border-r border-white/15 text-[11px] font-bold text-white focus:border-[#DC2626] outline-none">
              <option value="miami-dade">Miami-Dade</option>
              <option value="hillsborough">Hillsborough</option>
              <option value="broward">Broward</option>
            </select>
            <select value={paUsePrefix} onChange={(e) => setPaUsePrefix(e.target.value)}
              data-testid="smith-pa-use-prefix"
              className="h-9 px-2 bg-black/30 border-r border-white/15 text-[11px] font-bold text-white focus:border-[#DC2626] outline-none">
              <option value="48">48xx Warehouse</option>
              <option value="49">49xx Open Storage</option>
              <option value="41">41xx Light Mfg</option>
              <option value="42">42xx Heavy Ind</option>
            </select>
            <button type="button" onClick={scrapePropertyAppraiser} disabled={paScraping}
              data-testid="smith-pa-scrape-btn"
              title="Import Property Appraiser records by county + DOR use-code"
              className="h-9 px-3 font-bold text-[12.5px] text-white inline-flex items-center gap-1.5 disabled:opacity-50"
              style={{ background: "linear-gradient(135deg,#DC2626 0%,#7F1D1D 100%)" }}>
              {paScraping ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LandmarkIcon className="w-3.5 h-3.5" />}
              PA Scrape
            </button>
          </div>
          <button type="button" onClick={load} disabled={loading}
            data-testid="smith-refresh-btn"
            className="h-9 px-3 rounded-lg border border-white/15 text-[12.5px] font-bold hover:border-[#DC2626] inline-flex items-center gap-1.5">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <SmithImportExport filters={{}} onImported={load} testidPrefix="smith-dash-io" />
        </AppHeader>

        <main className="px-3 sm:px-6 lg:px-8 py-4 sm:py-6 max-w-[1400px] mx-auto space-y-5 pb-24 lg:pb-6"
              data-testid="smith-dashboard">
          {/* Iter268 · Install nudge banner (auto-hides if standalone or dismissed) */}
          <InstallNudgeBanner />

          {/* Iter214 — Sales Command Center (Fase 2) */}
          <SmithCommandCenter />

          {loading && !data ? (
            <div className="h-40 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-[#DC2626]" />
            </div>
          ) : !data || data.totals.prospects === 0 ? (
            <div className="rounded-2xl border p-8 text-center"
                 style={{ background: "rgba(30,58,138,0.15)", borderColor: "rgba(220,38,38,0.35)" }}>
              <Shield className="w-10 h-10 mx-auto mb-3 text-[#DC2626]" />
              <h2 className="text-[20px] font-extrabold text-white">Start with 5 demo prospects</h2>
              <p className="text-[13px] text-slate-300 mt-1.5 mb-4">
                Seed Orlando + Tampa demo accounts so you can explore the scoring engine, the map and the pipeline right away.
              </p>
              <button type="button" onClick={seed} disabled={seeding}
                data-testid="smith-seed-btn"
                className="h-11 px-6 rounded-lg font-bold text-[13px] text-white inline-flex items-center gap-2 disabled:opacity-60"
                style={{ background: "linear-gradient(135deg,#DC2626 0%,#991B1B 100%)" }}>
                {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
                Seed demo prospects
              </button>
            </div>
          ) : (
            <>
              {/* KPI row */}
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                <Kpi icon={Flame} color="#DC2626" label="Hot Prospects" value={data.totals.hot} testid="kpi-hot" />
                <Kpi icon={TrendingUp} color="#16A34A" label="High Priority" value={data.totals.high} testid="kpi-high" />
                <Kpi icon={DollarSign} color="#EAB308" label="Pipeline ACV" value={usd(data.pipeline_value.acv_sum)} testid="kpi-acv" />
                <Kpi icon={AlertTriangle} color="#F59E0B" label="Trigger Events" value={data.totals.triggers} testid="kpi-triggers" />
                {/* Iter237 · Enrichment Gatekeeper KPI */}
                <MissingContactKpi
                  missing={missing}
                  onEnrich={enrichHighValue}
                  busy={enrichingHV}
                />
              </div>

              {/* Today's top prospects */}
              <section className="rounded-2xl border overflow-hidden"
                       style={{ background: "rgba(15,23,42,0.7)", borderColor: "rgba(220,38,38,0.20)" }}>
                <div className="px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: "rgba(220,38,38,0.20)" }}>
                  <div className="flex items-center gap-2">
                    <Flame className="w-4 h-4 text-[#DC2626]" />
                    <h3 className="font-extrabold text-white text-[14px]">Today&apos;s Top Prospects</h3>
                  </div>
                  <Link to="/smith/prospects" data-testid="see-all-prospects"
                    className="text-[11.5px] text-[#DC2626] hover:underline inline-flex items-center gap-1">
                    See all <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
                <div className="divide-y" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
                  {data.top_today.map((p) => (
                    <Link key={p.id} to={`/smith/prospects/${p.id}`}
                      data-testid={`top-prospect-${p.id}`}
                      className="grid grid-cols-[80px_1fr_auto] gap-3 px-5 py-3 hover:bg-white/5 items-center">
                      <div className="flex items-center gap-1.5 text-[12px] font-extrabold tabular-nums"
                           style={{ color: p.tier.color }}>
                        <span>{p.tier.icon}</span>
                        <span>{p.opportunity_score}</span>
                      </div>
                      <div className="min-w-0">
                        <div className="text-[13.5px] font-bold text-white truncate">{p.company.name}</div>
                        <div className="text-[11px] text-slate-400 truncate">
                          {p.property.city}, {p.property.state} · {(p.segment || "").replace("_", " ")}
                          {p.security.current_provider && ` · Incumbent: ${p.security.current_provider}`}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-[12px] font-extrabold text-emerald-400">{p.opportunity.acv_bucket || "—"}</div>
                        <div className="text-[10.5px] text-slate-500">Est. ACV</div>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>

              {/* By segment / city / provider — 3-column analytics */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                <SmallList title="By Segment" icon={Building2} rows={data.by_segment.map((r) => ({ label: r.segment.replace("_", " "), count: r.count }))} />
                <SmallList title="By City" icon={MapPin} rows={data.by_city.map((r) => ({ label: r.city, count: r.count }))} />
                <SmallList title="Existing Providers" icon={Users} rows={data.by_provider.map((r) => ({ label: r.provider, count: r.count }))} emptyLabel="No provider data yet" />
              </div>

              {/* Iter174 — Multi-Site Accounts (cross-parcel owner detection) */}
              <MultiSiteAccountsCard />

              {/* Iter319 — Preferred-language distribution + admin bulk reset */}
              <LanguageMixCard />

              {/* Iter329 — Claire actions widget */}
              <ClaireActionsWidget />
            </>
          )}
        </main>
      </div>
    </div>
  );
}


function Kpi({ icon: Icon, color, label, value, testid }) {
  return (
    <div className="rounded-xl border p-4"
         data-testid={testid}
         style={{ background: "rgba(15,23,42,0.7)", borderColor: `${color}55` }}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4" style={{ color }} />
        <span className="text-[10.5px] font-bold uppercase tracking-widest" style={{ color }}>{label}</span>
      </div>
      <div className="text-[24px] font-extrabold text-white tabular-nums">{value}</div>
    </div>
  );
}

// Iter237 · Enrichment Gatekeeper KPI card — shows count of high-value
// leads (ACV ≥ $250k) still missing direct contact info, plus a one-click
// batch-enrich button. Sits alongside the standard KPI row.
function MissingContactKpi({ missing, onEnrich, busy }) {
  const total = missing?.high_value_total || 0;
  const gap = missing?.missing_contact || 0;
  const coverage = missing?.coverage_pct ?? null;
  const critical = gap > 0;
  return (
    <div className="rounded-xl border p-4"
         data-testid="kpi-missing-contact"
         style={{ background: "rgba(15,23,42,0.7)",
                    borderColor: critical ? "rgba(245,158,11,0.55)" : "rgba(16,185,129,0.45)" }}>
      <div className="flex items-center gap-2 mb-2">
        <MailWarning className="w-4 h-4" style={{ color: critical ? "#F59E0B" : "#10B981" }} />
        <span className="text-[10.5px] font-bold uppercase tracking-widest"
              style={{ color: critical ? "#F59E0B" : "#10B981" }}>
          Missing Contact
        </span>
      </div>
      <div className="flex items-baseline gap-1 tabular-nums">
        <span className={`text-[24px] font-extrabold ${critical ? "text-amber-300" : "text-emerald-300"}`}>
          {gap}
        </span>
        <span className="text-[13px] text-slate-500">/ {total} HV</span>
      </div>
      <div className="text-[10.5px] text-slate-400 mt-0.5">
        {coverage != null ? <>Coverage <b>{coverage}%</b> · ACV ≥ $250k</> : "ACV ≥ $250k sem contato"}
      </div>
      <button type="button" onClick={onEnrich}
        disabled={busy || gap === 0}
        data-testid="kpi-enrich-hv-btn"
        title="Roda enrichment cascade Apollo→Hunter→Apify em background"
        className="mt-2 h-7 w-full rounded text-[11px] font-bold text-white inline-flex items-center justify-center gap-1 disabled:opacity-50"
        style={{ background: critical
          ? "linear-gradient(135deg,#F59E0B 0%,#B45309 100%)"
          : "linear-gradient(135deg,#10B981 0%,#047857 100%)" }}>
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Rocket className="w-3 h-3" />}
        {gap === 0 ? "Cobertura 100%" : "Enrich High-Value"}
      </button>
    </div>
  );
}




function SmallList({ title, icon: Icon, rows, emptyLabel }) {
  return (
    <div className="rounded-2xl border p-4"
         style={{ background: "rgba(15,23,42,0.7)", borderColor: "rgba(220,38,38,0.20)" }}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-[#DC2626]" />
        <h4 className="font-bold text-white text-[13px]">{title}</h4>
      </div>
      {rows.length === 0 ? (
        <p className="text-[11.5px] text-slate-500 italic">{emptyLabel || "No data"}</p>
      ) : (
        <ul className="space-y-1.5">
          {rows.slice(0, 8).map((r, i) => (
            <li key={i} className="flex items-center justify-between text-[12.5px]">
              <span className="text-slate-200 capitalize truncate mr-2">{r.label}</span>
              <span className="text-[11px] font-bold text-slate-400 tabular-nums">{r.count}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
